import { freshProgress } from './domain.ts';
import { validProgress } from './progress-validation.ts';
import type { Progress } from './types.ts';
import type { MapState } from './maps.ts';

/** Provider-neutral wire/storage schema. Never run legacy migrations on remote documents. */
export type Notebook = { version: 1; progress: Progress; maps: Record<string, MapState> };
export type SavedNotebook = { document: Notebook; revision: number; updatedAt: string };
export const freshNotebook = (): Notebook => ({ version: 1, progress: freshProgress(), maps: {} });
export const MAX_DOCUMENT_BYTES = 512 * 1024;
export function normalizeUsername(value: string): string | null {
  const name = value.trim().toLowerCase();
  return /^[a-z0-9][a-z0-9_-]{1,31}$/.test(name) ? name : null;
}
const record = (v: unknown): v is Record<string, unknown> =>
  !!v && typeof v === 'object' && !Array.isArray(v);
export function validNotebook(v: unknown): v is Notebook {
  if (!record(v) || v.version !== 1 || !validProgress(v.progress) || !record(v.maps)) return false;
  return Object.entries(v.maps).every(
    ([key, state]) =>
      /^mentat\.maps\.v1:\d{4}-\d{2}-\d{2}:[a-z0-9-]+$/.test(key) &&
      record(state) &&
      Number.isInteger(state.stage) &&
      Number(state.stage) >= -1 &&
      Number(state.stage) <= 100 &&
      record(state.solved) &&
      Object.entries(state.solved).every(
        ([id, status]) =>
          /^\d{1,3}\/[a-zA-Z0-9_-]+$/.test(id) && (status === 'named' || status === 'revealed'),
      ),
  );
}
export function validSaved(v: unknown): v is SavedNotebook {
  return (
    record(v) &&
    validNotebook(v.document) &&
    Number.isSafeInteger(v.revision) &&
    Number(v.revision) > 0 &&
    typeof v.updatedAt === 'string'
  );
}
